import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  TextInput,
  ScrollView,
} from 'react-native';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import { useSelector, useDispatch } from 'react-redux';
import FastestTag from "@assets/svg/WithdrawGullakMoney/FastestTag";
import ButtonError from "@assets/svg/WithdrawGullakMoney/ButtonError";
import { withdrawMoney } from '@services/api';
import { actions as walletV3Actions } from '@reducers/walletV3Reducer';
import { ScaledSheet } from '@utils/scale';
import TransactionStatusModal from './TransactionStatusModal';
import WithdrawGullakMoneyHeader from '../WithdrawGullakMoney/components/Header';

const WithdrawMoney = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const dispatch = useDispatch();
  
  const { accountType, accountData } = route.params || {};
  const summary = useSelector((state) => state.walletV3Reducer?.walletSummary);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('');
  const [modalData, setModalData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const balance = summary?.value || 0;
  const isBankAccount = accountType === 'bank_account';

  useFocusEffect(
    useCallback(() => {
      fetchWalletBalance();
    }, []),
  );

  useEffect(() => {
    if (amount) {
      const numAmount = parseFloat(amount);
      const numBalance = parseFloat(balance);
      if (numAmount > numBalance) {
        setError('You have exceeded the amount');
      } else if (numAmount <= 0) {
        setError('Please enter a valid amount');
      } else {
        setError('');
      }
    } else {
      setError('');
    }
  }, [amount, balance]);

  const handleAmountChange = (text) => {
    // Allow integers and floating-point numbers
    // Remove all characters except digits and decimal point
    let cleaned = text.replace(/[^0-9.]/g, '');
    
    // Ensure only one decimal point
    const parts = cleaned.split('.');
    if (parts.length > 2) {
      cleaned = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Prevent leading zeros before non-decimal numbers (e.g., "00123" -> "123")
    if (cleaned.length > 1 && cleaned[0] === '0' && cleaned[1] !== '.') {
      cleaned = cleaned.replace(/^0+/, '');
    }
    
    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      cleaned = parts[0] + '.' + parts[1].substring(0, 2);
    }
    
    setAmount(cleaned);
  };

  const handleAddFullAmount = () => {
    setAmount(balance.toString());
  };

  const handleProceed = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    if (parseFloat(amount) > parseFloat(balance)) {
      setError('You have exceeded the amount');
      return;
    }

    setIsProcessing(true);
    setModalType('processing');
    setModalVisible(true);
    setModalData({
      title: 'Withdrawal Processing...',
    });

    try {
      const payload = {
        amount: parseFloat(amount),
        fund_account_id: accountData?.id,
        mode: accountData?.default_mode,
      };

      const response = await withdrawMoney(payload);      
      // Handle different response statuses
      if (response?.data?.status === 'success' || response?.data?.success) {
        setModalType('success');
        setModalData({
          title: 'Withdrawal Successful',
        });
      } else if (response?.data?.status === 'pending') {
        setModalType('pending');
        setModalData({
          title: 'Withdrawal Pending',
        });
      } else {
        setModalType('failed');
        setModalData({
          title: 'Withdrawal Failed',
        });
      }
    } catch (err) {
      setModalType('failed');
      setModalData({
        title: 'Withdrawal Failed',
        subtitle: err?.response?.data?.message,
      });
    } finally {
      setIsProcessing(false);
      fetchWalletBalance();
    }
  };

  const fetchWalletBalance = async () => {
      dispatch(walletV3Actions.setWalletV3Loading(true));
      try {
        const { data } = await getWalletSummaryV3();
        setWalletSummary(data);
        dispatch(walletV3Actions.getWalletV3SummarySuccess(data?.summary));
      } catch (error) {
        console.log(error);
        dispatch(walletV3Actions.getWalletV3SummaryFail(error));
      } finally {
        dispatch(walletV3Actions.setWalletV3Loading(false));
      }
    };

  const handleCloseModal = () => {
    setModalVisible(false);
    setAmount('');
    if (modalType === 'success') {
      navigation.goBack();
    }
  };

  const renderBankDetails = () => (
    <View style={styles.detailsCard}>
      <View style={styles.detailsHeader}>
        <Text style={styles.detailsTitle}>Bank Account Details</Text>
        <View style={styles.approvedBadge}>
          <Text style={styles.approvedText}>Approved</Text>
        </View>
      </View>

      <View style={styles.detailRow}>
        <Text style={styles.detailLabel}>Name</Text>
        <Text style={styles.detailLabel}>{accountData?.user_name || 'N/A'}</Text>
      </View>

      <View style={styles.detailRow}>
        <Text style={styles.detailLabel}>Account Number</Text>
        <Text style={styles.detailLabel}>{accountData?.account_number || 'N/A'}</Text>
      </View>

      <View style={styles.detailRow}>
        <Text style={styles.detailLabel}>IFSC Code</Text>
        <Text style={styles.detailLabel}>{accountData?.ifsc_code || 'N/A'}</Text>
      </View>
    </View>
  );

  const renderUPIDetails = () => (
    <View style={styles.detailsCard}>
      <View style={styles.detailsHeader}>
        <Text style={styles.detailsTitle}>Payment Details</Text>
        <View style={{paddingHorizontal: 4}}>
          <FastestTag />
        </View>
      </View>

      <View style={styles.detailRow}>
        <Text style={styles.detailLabel}>UPI ID</Text>
        <Text style={styles.detailLabel}>{accountData?.upi_address || 'N/A'}</Text>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <WithdrawGullakMoneyHeader navigation={navigation} route={route} />
      <ScrollView style={styles.content}>
        {isBankAccount ? renderBankDetails() : renderUPIDetails()}

        <View style={styles.amountCard}>
          <Text style={styles.amountLabel}>Amount</Text>
          <View style={styles.amountInputContainer}>
            <Text style={styles.currencySymbol}>₹</Text>
            <TextInput
              style={styles.amountInput}
              value={amount}
              onChangeText={handleAmountChange}
              keyboardType="numeric"
              placeholder="0"
              placeholderTextColor="#999"
            />
            <TouchableOpacity
              style={styles.addFullButton}
              onPress={handleAddFullAmount}
            >
              <Text style={styles.addFullText}>ADD Full Amount</Text>
            </TouchableOpacity>
          </View>
        </View>

        {!!error ? (
          <View style={styles.errorContainer}>
            <ButtonError />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : <View style={styles.errorContainer} />}

        <TouchableOpacity
          style={[
            styles.proceedButton,
            (!!error || !amount || isProcessing) && styles.proceedButtonDisabled,
          ]}
          onPress={handleProceed}
          disabled={!!error || !amount || isProcessing}
        >
          <Text style={styles.proceedButtonText}>Proceed</Text>
        </TouchableOpacity>
      </ScrollView>

      <TransactionStatusModal
        visible={modalVisible}
        data={{
          status: modalType,
          amount: amount,
          title: modalData?.title,
          subtitle: modalData?.subtitle || '',
        }}
        onClose={handleCloseModal}
      />
    </View>
  );
};

const styles = ScaledSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  gradientContainer: {
    paddingTop: 16,
    paddingBottom: 48,
    paddingHorizontal: 12,
  },
  headerTop: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  backButton: {
    padding: 8,
    marginRight: 12,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  balanceSection: {
    alignItems: 'center',
    paddingBottom: 24,
  },
  balanceLabel: {
    fontSize: 14,
    color: '#FFFFFF',
    opacity: 0.8,
    marginBottom: 8,
  },
  balanceAmount: {
    fontSize: 44,
    fontWeight: '900',
    color: '#FFFFFF',
  },
  content: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    marginTop: -24,
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  detailsCard: {
    backgroundColor: '#FFFFFF',
    padding: 8,
    marginBottom: 24,
  },
  detailsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  detailsTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#000',
  },
  approvedBadge: {
    backgroundColor: '#4CAF50',
    paddingHorizontal: 12,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 2,
    marginLeft: 4
  },
  approvedText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 4,
  },
  detailLabel: {
    fontSize: 12,
    color: '#666',
  },
  detailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#000',
  },
  amountCard: {
    marginBottom: 16,
  },
  amountLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 12,
  },
  amountInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#000000',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  currencySymbol: {
    fontSize: 24,
    fontWeight: '700',
    color: '#000',
    marginRight: 8,
  },
  amountInput: {
    flex: 1,
    fontSize: 24,
    fontWeight: '700',
    color: '#000',
    padding: 0,
  },
  addFullButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
  },
  addFullText: {
    fontSize: 14,
    fontWeight: '700',
    color: '#000',
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 30
  },
  errorIcon: {
    fontSize: 16,
    color: '#F44336',
    marginRight: 8,
  },
  errorText: {
    paddingLeft: 8,
    fontSize: 14,
    color: '#F44336',
  },
  proceedButton: {
    backgroundColor: '#2196F3',
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: 'center',
    marginTop: 8,
    marginBottom: 32,
  },
  proceedButtonDisabled: {
    backgroundColor: '#CCCCCC',
  },
  proceedButtonText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    padding: 32,
    alignItems: 'center',
    width: '90%',
    maxWidth: 400,
  },
  closeButton: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F0F0F0',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 18,
    color: '#666',
  },
  modalIcon: {
    marginBottom: 24,
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#4CAF50',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#4CAF50',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  checkmark: {
    fontSize: 48,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  failedIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#F44336',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#F44336',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  crossmark: {
    fontSize: 48,
    fontWeight: '700',
    color: '#FFFFFF',
  },
  pendingIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#2196F3',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#2196F3',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  clockIcon: {
    fontSize: 40,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#000',
    marginBottom: 8,
    textAlign: 'center',
  },
  modalSubtitle: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
});

export default WithdrawMoney;
